import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { storageSize } from '@wr/shared';
import { useToast } from '../../components/Toast.js';
import { Notice, PageHead } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import {
  IPHONE_REMINDER,
  askPersistent,
  isInstalledApp,
  isIos,
  keepItem,
  keptItems,
  kioskSources,
  offlineState,
  offlineVersion,
  preparation,
  spaceProblem,
  storageInfo,
  type KeptItem,
} from '../../offline/media.js';
import { useOnline } from '../../offline/network.js';
import { useSignedIn } from '../../session.js';
import type { KioskItem } from './data.js';

/**
 * Before the kiosk starts (round 2, brief section 7.4): its content is
 * downloaded on this device only once the person confirms it, with its
 * size (roaming); "Ready offline" once it is there. A video without an
 * offline copy is said to be hidden without network.
 */
export function KioskPrepare({
  eventId,
  name,
  items,
  kept,
  onKept,
  onStart,
}: {
  eventId: string;
  name: string;
  items: KioskItem[];
  kept: Record<string, KeptItem>;
  onKept(kept: Record<string, KeptItem>): void;
  onStart(): void;
}) {
  const { store } = useOffline();
  const { user } = useSignedIn();
  const online = useOnline();
  const navigate = useNavigate();
  const toast = useToast();
  const [free, setFree] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number; current: string } | null>(
    null,
  );
  const [failed, setFailed] = useState(0);
  const prep = preparation(items, kept);

  useEffect(() => {
    let current = true;
    void storageInfo().then(({ usage, quota }) => {
      if (current) setFree(usage !== null && quota !== null ? Math.max(0, quota - usage) : null);
    });
    return () => {
      current = false;
    };
  }, [kept]);

  async function download() {
    if (!store) return;
    const space = await spaceProblem(prep.size);
    if (space) return setProblem(space);
    setProblem(null);
    await askPersistent();
    let done = 0;
    let bad = 0;
    for (const a of prep.todo) {
      setProgress({ done, total: prep.size, current: a.title });
      try {
        await keepItem(
          store,
          user.id,
          a,
          (n) => setProgress({ done: done + n, total: prep.size, current: a.title }),
          kioskSources(eventId),
        );
      } catch {
        bad += 1;
      }
      done += offlineVersion(a)?.size ?? 0;
    }
    setProgress(null);
    setFailed(bad);
    const now = await keptItems(store).catch(() => kept);
    onKept(now);
    if (bad === 0) toast(`Ready offline: the booth kiosk of ${name} works without network here.`);
  }

  const busy = progress !== null;
  const pct = progress ? Math.round((progress.done / Math.max(1, progress.total)) * 100) : 0;
  return (
    <>
      <PageHead title="Booth kiosk" lede={name} />
      <section className="section card" aria-labelledby="kp-title">
        <h2 id="kp-title" className="sec">
          Offline on this device
        </h2>
        {prep.ready ? (
          <p>
            <span className="offline-badge">Ready offline</span> The kiosk works without network on
            this device.
          </p>
        ) : (
          <p>
            For the kiosk to work without network at the show, download its content on this device:{' '}
            {prep.todo.length} item{prep.todo.length === 1 ? '' : 's'}, {storageSize(prep.size)}
            {free !== null ? ` (${storageSize(free)} free on this device)` : ''}. Nothing is
            downloaded without you asking.
          </p>
        )}
        <div className="offline-list">
          {items.map((a) => {
            const state = offlineState(a, kept);
            return (
              <div key={a.id} className="purge-row">
                <span>
                  <b>{a.title}</b>
                  <span className={`note${state === 'unavailable' ? ' offline-none' : ''}`}>
                    {a.format === 'video' ? 'Video' : 'PDF'},{' '}
                    {state === 'unavailable'
                      ? 'no offline copy: hidden in the kiosk without network'
                      : state === 'ready'
                        ? 'ready offline'
                        : state === 'update'
                          ? `update available, ${storageSize(offlineVersion(a)?.size ?? 0)}`
                          : storageSize(offlineVersion(a)?.size ?? 0)}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
        {isIos() && !isInstalledApp() && <Notice kind="warn">{IPHONE_REMINDER}</Notice>}
        {!online && !prep.ready && <Notice kind="warn">No network: downloading needs it.</Notice>}
        {problem && <Notice kind="error">{problem}</Notice>}
        {failed > 0 && (
          <Notice kind="error">
            {failed} item{failed === 1 ? '' : 's'} not downloaded: check the network and try again.
          </Notice>
        )}
        {progress && (
          <div className="uprow up" aria-live="polite">
            <div className="nm">
              <span>{progress.current}</span>
              <span>
                {storageSize(progress.done)} of {storageSize(progress.total)} ({pct}%)
              </span>
            </div>
            <div className="bar" role="progressbar" aria-label="Downloaded" aria-valuenow={pct}>
              <i style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
        <div className="actions">
          {!prep.ready && (
            <button
              type="button"
              className="btn primary"
              disabled={busy || !online || !store}
              onClick={() => void download()}
            >
              Download ({storageSize(prep.size)})
            </button>
          )}
          <button
            type="button"
            className={`btn${prep.ready ? ' primary' : ''}`}
            disabled={busy}
            onClick={onStart}
          >
            {prep.ready ? 'Start booth kiosk' : 'Start without downloading'}
          </button>
          <button type="button" className="btn" disabled={busy} onClick={() => navigate('/events')}>
            Back to Events
          </button>
        </div>
      </section>
    </>
  );
}
